/**
 * RollingSharpChart — rolling annualised Sharpe for 3m / 6m / 12m windows.
 *
 * Props:
 *   data    [{date, sharpe_3m, sharpe_6m, sharpe_12m}]
 *   height  number  SVG height (default 200)
 */

import { CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const SERIES = [
  { key: 'sharpe_3m',  label: '3 month',  color: '#38bdf8' },  // sky-400
  { key: 'sharpe_6m',  label: '6 month',  color: '#a78bfa' },  // violet-400
  { key: 'sharpe_12m', label: '12 month', color: '#fb923c' },  // orange-400
];

export default function RollingSharpChart({ data = [], height = 200 }) {
  if (!data.length) return null;

  // Collect all non-null values across all series to set y-axis range
  const allVals = data.flatMap(pt =>
    SERIES.map(s => pt[s.key]).filter(v => v != null)
  );
  if (!allVals.length) return null;

  // Check which series have any data (to avoid showing legend for all-null series)
  const activeSeries = SERIES.filter(s =>
    data.some(pt => pt[s.key] != null)
  );

  const chartData = data.map((c) => ({
    ...c,
    zero_line: 0
  }))

  return (
    <div className="flex flex-col gap-3">

      <div className="w-full text-xs" style={{height: `${height}px`}}> 
        <ResponsiveContainer>
            <ComposedChart
            data={chartData}
            margin={{ top: 10, right: 0, left: -25, bottom: 0 }}
            >
              <CartesianGrid 
                strokeWidth={1} strokeOpacity={0.5}
                strokeDasharray="3 3" vertical={false}/>

              <YAxis
                axisLine={false} 
                tickLine={false}
                domain={['dataMin', 'auto']}
                tick={{fill: 'hsl(var(--foreground))'}}
                minTickGap={10}
              />

              <XAxis
                dataKey="date"
                axisLine={false} 
                dy={10}
                tick={{fill: 'hsl(var(--foreground))'}}
                minTickGap={10}
              />

              <Tooltip
                labelStyle={{ color: 'hsl(var(--foreground))' }}
                contentStyle={{background: 'hsl(var(--background))', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              />

              {SERIES.map((s)=> {
                return <Line
                    dataKey={s.key}
                    stroke={s.color}
                    strokeWidth={1}
                    dot={false}
                    name={s.label}
                />
              })}

              <Line
                dataKey='zero_line'
                stroke='hsl(var(--primary))'
                strokeWidth={1}
                dot={false}
                strokeDasharray="4"
                tooltipType="none"
              />
            </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs">
        {activeSeries.map(s => (
          <span key={s.key} className="flex items-center gap-1.5">
            <span className="inline-block w-6 h-0.5 rounded" style={{ background: s.color }} />
            <span className="text-muted-foreground">{s.label}</span>
          </span>
        ))}
      </div>

      <p className="text-[10px] text-muted-foreground">
        Annualised Sharpe on rolling windows. 12m window requires ~1 year of data to appear.
        Shorter windows visible sooner but noisier.
      </p>
    </div>
  );
}
